import {
  LS_FULL_STATE,
  LS_OAUTH_ERROR_KEY,
  LS_OAUTH_ONE_TIME_CODE,
  LS_OAUTH_REDIRECT_FINISHED,
} from "@superblocksteam/shared";
import type {
  AuthConfig,
  DatasourceAuthState,
  DatasourceOneTimeState,
} from "@superblocksteam/shared";

export type OAuthCallbackHandoff = "localStorage" | "postMessage";

type OAuthCallbackStorage = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export function resolveOAuthCallbackHandoff(
  authState: DatasourceOneTimeState | DatasourceAuthState,
): OAuthCallbackHandoff {
  return authState.useLocalStorage === true ? "localStorage" : "postMessage";
}

export function completeLocalStorageOAuthCallback({
  error,
  storage,
}: {
  error?: string;
  storage: OAuthCallbackStorage;
}): void {
  if (error) {
    storage.setItem(LS_OAUTH_ERROR_KEY, error);
  } else {
    storage.removeItem(LS_OAUTH_ERROR_KEY);
  }
  storage.setItem(LS_OAUTH_REDIRECT_FINISHED, "true");
  storage.removeItem(LS_FULL_STATE);
}

export function validateLocalStorageOneTimeCodeIfApplicable(
  state: DatasourceOneTimeState,
  authConfig: AuthConfig,
  storage: OAuthCallbackStorage,
): boolean {
  if (!authConfig.sendOAuthState) {
    return true;
  }
  return state.oneTimeCode === storage.getItem(LS_OAUTH_ONE_TIME_CODE);
}

export function validateOAuthCallbackOneTimeCodeIfApplicable({
  authConfig,
  handoffMode,
  postMessageOneTimeCodeVerified = false,
  state,
  storage,
}: {
  authConfig: AuthConfig;
  handoffMode: OAuthCallbackHandoff;
  postMessageOneTimeCodeVerified?: boolean;
  state: DatasourceOneTimeState;
  storage: OAuthCallbackStorage;
}): boolean {
  if (!authConfig.sendOAuthState) {
    return true;
  }
  if (handoffMode === "postMessage" && postMessageOneTimeCodeVerified) {
    return true;
  }
  if (handoffMode === "postMessage") {
    // Deployed-shell / embed openers put the nonce in OAuth state and
    // correlate postMessage by oneTimeCode. The popup's localStorage
    // partition is not shared with the opener (3PCD / ITP), so the
    // legacy opener-seeded key is absent even for valid flows.
    return Boolean(state.oneTimeCode);
  }
  return validateLocalStorageOneTimeCodeIfApplicable(
    state,
    authConfig,
    storage,
  );
}
